import React, { useEffect, useCallback } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { ValidationProject } from '../../types/validation';
import { formatStatus, formatCategory } from '../../data/validationData';

interface ProjectDetailModalProps {
  project: ValidationProject;
  onClose: () => void;
}

const ProjectDetailModal: React.FC<ProjectDetailModalProps> = ({ project, onClose }) => {
  // Handle escape key to close modal
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [handleKeyDown]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="project-detail-title"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 id="project-detail-title" className="text-xl font-bold text-gray-900">{project.name}</h2>
            <div className="flex items-center gap-2 mt-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                project.type === 'new' ? 'bg-cyan-100 text-cyan-800' : 'bg-amber-100 text-amber-800'
              }`}>
                {project.type === 'new' ? 'New Project' : 'Historic Project'}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                project.status === 'clean' ? 'bg-green-100 text-green-800' :
                project.status === 'stage1' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
              }`}>
                {formatStatus(project.status)}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Project Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Information</h3>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-xs text-gray-500 uppercase">Project ID</dt>
                <dd className="text-sm font-medium text-gray-900">{project.id}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase">Category</dt>
                <dd className="text-sm font-medium text-gray-900">{formatCategory(project.category)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase">Basin</dt>
                <dd className="text-sm font-medium text-gray-900">{project.details.basin}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase">Volumetric Water Benefit</dt>
                <dd className="text-sm font-medium text-gray-900">{project.details.volumetricWaterBenefit}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase">Cost-Share</dt>
                <dd className="text-sm font-medium text-gray-900">{project.details.costShare}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 uppercase">Quantification Method</dt>
                <dd className="text-sm font-medium text-gray-900">{project.details.quantificationMethod}</dd>
              </div>
            </dl>
          </div>

          {/* Previous Year Comparison */}
          {project.details.previousYear && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Previous Year Comparison</h3>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-xs text-gray-500 uppercase">Previous Volumetric Water Benefit</dt>
                  <dd className="text-sm font-medium text-gray-900">{project.details.previousYear.volumetricWaterBenefit}</dd>
                </div>
                {project.details.previousYear.costShare && (
                  <div>
                    <dt className="text-xs text-gray-500 uppercase">Previous Cost-Share</dt>
                    <dd className="text-sm font-medium text-gray-900">{project.details.previousYear.costShare}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Validation Flags */}
          {project.flags.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Validation Flags</h3>
              {project.flags.map((flag, index) => (
                <div key={index} className={`p-4 rounded-lg border-l-4 ${
                  project.status === 'stage2' ? 'bg-red-50 border-red-500' : 'bg-amber-50 border-amber-500'
                } mb-3`}>
                  <h4 className="font-semibold text-gray-900">{flag.type}</h4>
                  <p className="text-sm text-gray-600 mt-1">{flag.description}</p>
                  <dl className="mt-2 text-sm space-y-1">
                    <div><dt className="inline font-semibold">Affected Field:</dt> <dd className="inline">{flag.field}</dd></div>
                    {flag.systemValue && <div><dt className="inline font-semibold">System Value:</dt> <dd className="inline">{flag.systemValue}</dd></div>}
                    {flag.factSheetValue && <div><dt className="inline font-semibold">Fact Sheet Value:</dt> <dd className="inline">{flag.factSheetValue}</dd></div>}
                    {flag.expectedValue && <div><dt className="inline font-semibold">Expected:</dt> <dd className="inline">{flag.expectedValue}</dd></div>}
                    {flag.requirement && <div><dt className="inline font-semibold">Requirement:</dt> <dd className="inline">{flag.requirement}</dd></div>}
                    {flag.riskLevel && <div><dt className="inline font-semibold">Risk Level:</dt> <dd className="inline">{flag.riskLevel}</dd></div>}
                    {flag.previousValue && <div><dt className="inline font-semibold">Previous Year:</dt> <dd className="inline">{flag.previousValue}</dd></div>}
                    {flag.change && <div><dt className="inline font-semibold">Change:</dt> <dd className="inline">{flag.change}</dd></div>}
                  </dl>
                </div>
              ))}
            </div>
          )}

          {/* Clean Status */}
          {project.status === 'clean' && (
            <div className="p-4 rounded-lg bg-green-50 border-l-4 border-green-500">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" aria-hidden="true" />
                <h4 className="font-semibold text-green-800">Validation Status: Clean</h4>
              </div>
              <p className="text-sm text-green-700 mt-1">This project has passed all automated validation checks and meets VWBA 2.0 requirements. No manual review required.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailModal;
